import { useContext, useMemo, useEffect } from 'react';

import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

import { CameraSetup } from '@data';

import { getCoverSourceRect } from '../3d/utils';
import { MediaSizeContext } from '../components/context';

export function OpenCVDistortionPass({ config }: { config: CameraSetup }) {
    const { gl, scene, camera, size } = useThree();
    const mediaSize = useContext(MediaSizeContext);

    const renderTarget = useMemo(
        () =>
            new THREE.WebGLRenderTarget(1, 1, {
                depthBuffer: true,
                stencilBuffer: false,
                format: THREE.RGBAFormat,
                type: THREE.UnsignedByteType,
            }),
        [],
    );

    const postScene = useMemo(() => new THREE.Scene(), []);
    const postCamera = useMemo(() => new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1), []);
    const material = useMemo(
        () =>
            new THREE.ShaderMaterial({
                transparent: true,
                depthTest: false,
                depthWrite: false,
                uniforms: {
                    tDiffuse: { value: null },
                    sourceRect: { value: new THREE.Vector4() },
                    intrinsics: { value: new THREE.Vector4() },
                    distortion: { value: new THREE.Vector4() },
                    k3: { value: 0 },
                },
                vertexShader: `
                    varying vec2 vUv;

                    void main() {
                        vUv = uv;
                        gl_Position = vec4(position.xy, 0.0, 1.0);
                    }
                `,
                fragmentShader: `
                    uniform sampler2D tDiffuse;
                    uniform vec4 sourceRect;
                    uniform vec4 intrinsics;
                    uniform vec4 distortion;
                    uniform float k3;

                    varying vec2 vUv;

                    vec2 undistort(vec2 distortedPoint) {
                        vec2 point = distortedPoint;

                        for (int i = 0; i < 6; i++) {
                            float k1 = distortion.x;
                            float k2 = distortion.y;
                            float p1 = distortion.z;
                            float p2 = distortion.w;
                            float x = point.x;
                            float y = point.y;
                            float r2 = x * x + y * y;
                            float radial = 1.0 + k1 * r2 + k2 * r2 * r2 + k3 * r2 * r2 * r2;
                            vec2 tangential = vec2(
                                2.0 * p1 * x * y + p2 * (r2 + 2.0 * x * x),
                                p1 * (r2 + 2.0 * y * y) + 2.0 * p2 * x * y
                            );

                            point = (distortedPoint - tangential) / radial;
                        }

                        return point;
                    }

                    void main() {
                        float fx = intrinsics.x;
                        float fy = intrinsics.y;
                        float cx = intrinsics.z;
                        float cy = intrinsics.w;

                        vec2 distortedImagePoint = vec2(
                            sourceRect.x + vUv.x * sourceRect.z,
                            sourceRect.y + (1.0 - vUv.y) * sourceRect.w
                        );
                        vec2 distortedNormalized = vec2(
                            (distortedImagePoint.x - cx) / fx,
                            (distortedImagePoint.y - cy) / fy
                        );
                        vec2 undistortedNormalized = undistort(distortedNormalized);
                        vec2 undistortedImagePoint = vec2(
                            undistortedNormalized.x * fx + cx,
                            undistortedNormalized.y * fy + cy
                        );
                        vec2 sampleUv = vec2(
                            (undistortedImagePoint.x - sourceRect.x) / sourceRect.z,
                            1.0 - (undistortedImagePoint.y - sourceRect.y) / sourceRect.w
                        );

                        if (sampleUv.x < 0.0 || sampleUv.x > 1.0 || sampleUv.y < 0.0 || sampleUv.y > 1.0) {
                            discard;
                        }

                        gl_FragColor = texture2D(tDiffuse, sampleUv);
                    }
                `,
            }),
        [],
    );

    useEffect(() => {
        const geometry = new THREE.PlaneGeometry(2, 2);
        const quad = new THREE.Mesh(geometry, material);
        postScene.add(quad);

        return () => {
            postScene.remove(quad);
            geometry.dispose();
        };
    }, [material, postScene]);

    useEffect(
        () => () => {
            material.dispose();
            renderTarget.dispose();
        },
        [material, renderTarget],
    );

    useEffect(() => {
        const dpr = gl.getPixelRatio();
        renderTarget.setSize(Math.max(1, Math.floor(size.width * dpr)), Math.max(1, Math.floor(size.height * dpr)));
    }, [gl, renderTarget, size.height, size.width]);

    useEffect(() => {
        const { fx, fy, cx, cy, distortionCoeffs = [] } = config.intrinsics;
        const [k1 = 0, k2 = 0, p1 = 0, p2 = 0, k3 = 0] = distortionCoeffs;
        const sourceRect = getCoverSourceRect(
            config.intrinsics.width,
            config.intrinsics.height,
            mediaSize.width,
            mediaSize.height,
        );

        material.uniforms['sourceRect'].value.set(
            sourceRect.sourceX,
            sourceRect.sourceY,
            sourceRect.sourceWidth,
            sourceRect.sourceHeight,
        );
        material.uniforms['intrinsics'].value.set(fx, fy, cx, cy);
        material.uniforms['distortion'].value.set(k1, k2, p1, p2);
        material.uniforms['k3'].value = k3;
    }, [config, material, mediaSize.height, mediaSize.width]);

    useFrame(() => {
        const clearColor = new THREE.Color();
        gl.getClearColor(clearColor);
        const clearAlpha = gl.getClearAlpha();
        const autoClear = gl.autoClear;

        material.uniforms['tDiffuse'].value = renderTarget.texture;

        gl.autoClear = true;
        gl.setClearColor(0x000000, 0);
        gl.setRenderTarget(renderTarget);
        gl.clear();
        gl.render(scene, camera);

        gl.setRenderTarget(null);
        gl.clear();
        gl.render(postScene, postCamera);

        gl.setClearColor(clearColor, clearAlpha);
        gl.autoClear = autoClear;
    }, 1);

    return null;
}
